import { useState } from "react";
import { Loader2, Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { PhoneInput } from "@/components/PhoneInput";
import { BirthdatePicker, ageOf } from "@/components/BirthdatePicker";
import { PHONE_COUNTRIES, toE164 } from "@/lib/phoneCountries";
import { api } from "@/lib/appresse";
import { useAuth } from "@/lib/auth";

const pad = (n) => String(n).padStart(2, "0");
const parseBd = (s) => {
  const [y, m, d] = (s || "").split("-").map(Number);
  return y ? { d, m, y } : { d: "", m: "", y: "" };
};

// Client payant (titulaire + invités DUO/FAMILLE) : portable + date de naissance obligatoires.
export function PaidInfoPrompt() {
  const { user, refreshUser } = useAuth();
  const [country, setCountry] = useState(user?.phone_country || "FR");
  const [phone, setPhone] = useState("");
  const [bd, setBd] = useState(parseBd(user?.birthdate));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const c = PHONE_COUNTRIES.find((x) => x.code === country);
    const e164 = toE164(phone, c);
    if (!e164) return toast.error(country === "FR" ? "Numéro invalide : un portable commence par 06 ou 07" : "Numéro de portable invalide");
    if (ageOf(bd) === null) return toast.error("Choisissez votre jour, mois et année de naissance");
    setSaving(true);
    try {
      await api.savePaidInfo({ phone_mobile: e164, phone_country: country, birthdate: `${bd.y}-${pad(bd.m)}-${pad(bd.d)}` });
      await refreshUser();
      toast.success("Merci ! Bonne lecture.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open>
      <DialogContent className="sm:max-w-md rounded-2xl [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()} data-testid="paid-info-dialog">
        <div className="space-y-5">
          <DialogHeader className="items-center text-center space-y-2">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Smartphone className="w-7 h-7 text-primary" />
            </div>
            <DialogTitle className="font-serif font-black text-xl">Dernière étape avant la lecture</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Indiquez votre numéro de portable et votre date de naissance pour finaliser votre Espace.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Numéro de portable</Label>
            <PhoneInput country={country} onCountry={setCountry} value={phone} onChange={setPhone} />
          </div>
          <div className="space-y-2">
            <Label>Date de naissance</Label>
            <BirthdatePicker value={bd} onChange={setBd} />
          </div>
          <Button className="w-full gap-2" disabled={saving} onClick={save} data-testid="paid-info-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Valider et lire
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
